import api from '@/services/api-web-worker'
import denormalizeResponse from '@/utils/denormalizeResponse'
import mapValues from 'lodash/mapValues';
import { eventDurationString } from "@/components/EventBeacon/Utils/eventbeaconDateUtils";
import { sortByBestMatch } from "@/components/EventBeacon/Utils/bestMatchUtils";
import PromiseQueue from '@/services/promiseQueue';
import {createProfileView} from "@/services/viewTracker";

let countFetchStateTracker = [];

const favoritedAthletes = {
    id: true,
    firstName: true,
    lastName: true,
    favoritesReceived: {
        id: true
    },
    interests: {
        id: true,
    },
    isVerified: true,
    positions: true,
    eventTeamAffiliations: {
        id: true,
        team: {
            name: true,
            event: {
                id: true,
            }
        },
    },
    images: {
        id: true,
        isProfile: true,
        url: true,
    },
    urlSlug: true,
    classYear: true,
}

const interestedAthletes = {
    id: true,
    firstName: true,
    lastName: true,
    favoritesReceived: {
        id: true
    },
    interests: {
        id: true,
    },
    isVerified: true,
    positions: true,
    eventTeamAffiliations: {
        id: true,
        team: {
            name: true,
            event: {
                id: true
            }
        },
    },
    images: {
        id: true,
        isProfile: true,
        url: true,
    },
    urlSlug: true,
    classYear: true,
}

export async function fetchEvents({ commit, state }) {
    commit('setFetchState', true);
    const filters = { vodAvailable: 1 };

    const appliedEventsFiltersEvents = state.appliedEventsFilters.filter(filter => filter.type == "events");
    if (appliedEventsFiltersEvents.length) {
        filters.idOneOf = appliedEventsFiltersEvents.map(filter => filter.value);
    }

    const appliedTeamsFilters = state.appliedEventsFilters.filter(filter => filter.type == "teams");
    if (appliedTeamsFilters.length) {
        filters.containsTeam = appliedTeamsFilters.map(filter => filter.value);
    }

    const eventResponse = await api.get('/api/v2/resources/events', {
        query: {
            filter: filters,
            laravelPaginate: {
                perPage: 20,
                page: state.pagination.events.current_page,
            },
            sort: [
                {
                    dimension: "startDate",
                    direction: "desc",
                },
            ],
            include: {
                id: true,
                name: true,
                locationName: true,
                locationCity: true,
                locationState: true,
                startDate: true,
                endDate: true,
                interestedAthleteCount: true,
                favoritedAthleteCount: true,
                created: true,
                teamsWithVideoCount: true,
                gamesWithVideoCount: true,
                urlSlug: true,
                vodAvailable: true,
                gender: true,
                image: {
                    url: true,
                }
            },
        }
    });
    
    commit('setEventPaginationData', eventResponse.meta);
    commit('storeResources', eventResponse.resources);
    commit('setEventListing', eventResponse.root);
    commit('setFetchState', false);
}

export async function fetchGames({ commit, state }) {
    commit('setFetchState', true);
    commit('setGameListing', []);
    const filters = { vodAvailable: 1, hasGameVideo: true };

    const appliedPlayersFilters = state.appliedAthleteTeamFilters.filter(filter => filter.type == "athletes");
    if (appliedPlayersFilters.length) {
        filters.hasOneOfAthletes = appliedPlayersFilters.map(filter => filter.value);
    }

    const appliedTeamsFilters = state.appliedAthleteTeamFilters.filter(filter => filter.type == "teams");

    if (appliedTeamsFilters.length) {
        filters.hasOneTeam = appliedTeamsFilters.map(filter => filter.value);
    }

    const gamesResponse = await api.get('/api/v2/resources/games', {
        query: {
            filter: filters,
            include: {
                startTime: true,
                homeTeam: {
                    name: true,
                    division: {
                        id: true,
                        name: true
                    }
                },
                awayTeam: {
                    name: true,
                    division: {
                        id: true,
                        name: true
                    }
                },
                favoritedAthleteCount: true,
                interestedAthleteCount: true,
                favoritedAthletes,
                interestedAthletes,
                videos: {
                    id: true,
                    name: true,
                    host: true,
                    hostId: true,
                    duration: true,
                    thumbnailPath: true,
                    videoUrl: true,
                    videoType: true,
                },
                event: {
                    id: true,
                    name: true,
                    locationName: true,
                    locationCity: true,
                    locationState: true,
                    startDate: true,
                    endDate: true,
                    gamesWithVideoCount: true,
                    gender: true,
                    image: {
                        url: true,
                    },
                    urlSlug: true,
                }
            },
        }
    });
    commit('storeResources', gamesResponse.resources);
    commit('setGameListing', gamesResponse.root);
    commit('setFetchState', false);
}

export async function fetchSixGames({ commit, state }, options = {}) {
    const defaults = {
        offset: 0,
        reset: false,
    };
    const { offset, reset } = {
        ...defaults,
        ...options
    };

    commit('setFetchState', true);
    const filters = { vodAvailable: 1, hasGamesWithVideo: 1 };

    const gamesResponse = await api.get('/api/v2/resources/events', {
        query: {
            filter: filters,
            paginate: {
                limit: 5,
                offset,
            },
            sort: [
                {
                    dimension: 'startDate',
                    direction: 'desc',
                },
            ],
            include: {
                id: true,
                name: true,
                locationName: true,
                locationCity: true,
                locationState: true,
                startDate: true,
                endDate: true,
                gamesWithVideoCount: true,
                image: {
                    url: true,
                },
                urlSlug: true,
                gender: true,
                gamesWithVideo: {
                    "@paginate": {
                        limit: 6,
                        offset: 0
                    },
                    startTime: true,
                    homeTeam: {
                        name: true,
                        division: {
                            id: true,
                            name: true
                        }
                    },
                    awayTeam: {
                        name: true,
                        division: {
                            id: true,
                            name: true
                        }
                    },
                    favoritedAthleteCount: true,
                    interestedAthleteCount: true,
                    videos: {
                        id: true,
                        name: true,
                        host: true,
                        hostId: true,
                        duration: true,
                        thumbnailPath: true,
                        videoUrl: true,
                        videoType: true,
                    },
                    event: {
                        id: true
                    }
                }
            },
        }
    });

    const gamesListing = denormalizeResponse(gamesResponse).root
        .map(x => x.gamesWithVideo)
        .flat()
        .map(x => ({ type: 'games', id: parseInt(x.id) }));

    commit('storeResources', gamesResponse.resources);
    if (reset) {
        commit('setGameListing', gamesListing);
    } else {
        commit('addToGameListing', gamesListing);
    }
    commit('setFetchState', false);
}

export async function fetchGame({ commit, state }, gameId) {
    commit('setFetchState', true);
    const filters = { vodAvailable: 1 };

    const appliedPlayersFilters = state.appliedAthleteTeamFilters.filter(filter => filter.type == "athletes");

    if (appliedPlayersFilters.length) {
        filters.hasOneOfAthletes = appliedPlayersFilters.map(filter => filter.value);
    }

    const appliedTeamsFilters = state.appliedAthleteTeamFilters.filter(filter => filter.type == "teams");

    if (appliedTeamsFilters.length) {
        filters.hasOneTeam = appliedTeamsFilters.map(filter => filter.value);
    }

    const teamIncludes = {
        id: true,
        name: true,
        division: {
            id: true,
            name: true
        },
        athleteAffiliations: {
            id: true,
            number: true,
            position: true,
            athlete: {
                firstName: true,
                lastName: true,
                urlSlug: true,
                classYear: true,
                images: {
                    id: true,
                    url: true,
                    isProfile: true,
                },
                favoritesReceived: {
                    id: true,
                },
                skips: {
                    id: true,
                },
                positions: true,
                commitmentCollegeName: true
            },
            jerseyNumber: true,
        },
        staff: {
            id: true,
            firstName: true,
            lastName: true,
            position: true,
            phoneNumber: true,
            emailAddress: true,
        }
    };
    const gameResponse = await api.get(`/api/v2/resources/games/${gameId}`, {
        query: {
            filter: filters,
            include: {
                id: true,
                startTime: true,
                videos: {
                    id: true,
                    name: true,
                    host: true,
                    hostId: true,
                    duration: true,
                    thumbnailPath: true,
                    videoUrl: true,
                    videoType: true,
                },
                event: {
                    name: true,
                    id: true,
                    urlSlug: true,
                    gender: true,
                    image: {
                        url: true,
                    },
                    sport: {
                        id: true,
                        name: true,
                    }
                },
                homeTeam: teamIncludes,
                awayTeam: teamIncludes
            }
        }
    });

    commit('storeResources', gameResponse.resources);
    commit('setFetchState', false);
}

export async function fetchGameBasics({ commit }, gameId) {
    commit('setFetchState', true);

    const gameResponse = await api.get(`/api/v2/resources/games/${gameId}`, {
        query: {
            include: {
                id: true,
                event: {
                    id: true,
                    name: true,
                    locationName: true,
                    locationCity: true,
                    locationState: true,
                    startDate: true,
                    endDate: true,
                    interestedAthleteCount: true,
                    favoritedAthleteCount: true,
                    urlSlug: true,
                    vodAvailable: true,
                    gender: true,
                    image: {
                        url: true,
                    },
                    sport: {
                        id: true,
                        name: true,
                    }
                },
            }
        }
    });

    commit('storeResources', gameResponse.resources);
    commit('setFetchState', false);
}

export async function favoriteAthlete({ commit, state, rootState, source }, athleteId) {
    const cachedSkips = state.resources.athletes[athleteId].skips;
    commit('setFavorite', { athleteId, favorite: {} });
    try {
        const response = await api.post('/api/v2/athlete-favorites', {
            athleteId,
            location: 'ebvod',
            query: {
                include: {
                    id: true
                }
            }
        });
        createProfileView({ resource_id: athleteId, source: source });
        commit('storeResources', response.resources);
        commit('setFavorite', { athleteId, favorite: response.root });
    } catch (e) {
        commit('favoriteFailed', { cachedSkips, athleteId });
    }


}

export async function skipAthlete({ commit, state }, athleteId) {
    const cachedFavoritesReceived = state.resources.athletes[athleteId].favoritesReceived;

    commit('setSkipped', { skip: {}, athleteId });

    try {
        const response = await api.post('/api/v2/skips', {
            athleteId,
            location: 'ebvod',
            query: {
                include: {
                    id: true
                }
            }
        })
        commit('storeResources', response.resources);
        commit('setSkipped', { skip: response.root, athleteId });
    } catch (e) {
        commit('skipFailed', { cachedFavoritesReceived, athleteId });
    }
}

export async function fetchEventsFilters({ commit }, query = "") {
    commit('setFilterFetchState', true);
    const filterResponses = await Promise.all([
        api.get('/api/v2/resources/events', {
            query: {
                filter: {
                    vodAvailable: 1,
                    nameLike: `%${query.trim()}%`
                },
                include: {
                    id: true,
                    name: true,
                    startDate: true,
                    endDate: true,
                },
            }
        })
    ]);

    let events = denormalizeResponse(filterResponses[0]).root;
    events = sortByBestMatch(events, query, 'name');

    const filters = [
        {
            title: "Events",
            options: events.map(event => ({
                value: event.id,
                title: event.name,
                description: eventDurationString(event.startDate, event.endDate),
                selected: false,
                type: 'events'
            }))
        },
    ];

    commit('setAvailableEventsFilters', filters);
    commit('setFilterFetchState', false);
}

export async function fetchGamesFilters({ commit, rootState }, query = "") {
    commit('setFilterFetchState', true);
    const { user } = rootState.userData;
    const userGender = user.program.gender == "male" ? 1 : 2;
    const userSport = user.sport.id;
    const filterResponses = await Promise.all([
        api.get('/api/v2/resources/athletes', {
            query: {
                filter: {
                    search: query,
                    attendingAnyEvent: true,
                    attendingAnyEventWithVodAvailable: true,
                    sport: userSport,
                    gender: userGender
                },
                include: {
                    id: true,
                    firstName: true,
                    lastName: true,
                    isVerified: true,
                    positions: true,
                    classYear: true,
                    teamAffiliations: {
                        id: true,
                        team: {
                            id: true,
                            name: true,
                        }
                    }
                },
            }
        }),
        api.get('/api/v2/resources/event-teams', {
            query: {
                filter: {
                    nameLike: `%${query.trim()}%`,
                    attendingAnyEventWithVodAvailable: true
                },
                include: {
                    id: true,
                    name: true,
                    event: {
                        name: true,
                        id: true
                    }
                }
            }
        })
    ]);

    let athletes = denormalizeResponse(filterResponses[0]).root.map(x => ({
        ...x,
        fullName: `${x.firstName} ${x.lastName}`
    }));
    athletes = sortByBestMatch(athletes, query, 'fullName');

    let teams = denormalizeResponse(filterResponses[1]).root;
    teams = sortByBestMatch(teams, query, 'name');

    const filters = [
        {
            title: "Teams",
            options: teams.map(team => ({
                value: team.id,
                title: team.name,
                description: `at ${team.event.name}`,
                selected: false,
                type: 'teams',
            }))
        },
        {
            title: "Athletes",
            options: athletes.map(athlete => ({
                value: athlete.id,
                // Not using the ds class here because I don't want line-height to be set.
                pillTitle: `${athlete.firstName} ${athlete.lastName}`,
                title: `${athlete.firstName} ${athlete.lastName}${athlete.classYear ? `, <span class="text-xs">${athlete.classYear}</span>` : ""}`,
                description: athlete.positions.join(", ") + "<br/>" + athlete.teamAffiliations.map(aff => aff.team.name).join(", "),
                selected: false,
                type: 'athletes',
                meta: {
                    isVerified: athlete.isVerified
                }
            }))
        },
    ];

    commit('setAvailableAthleteTeamFilters', filters);
    commit('setFilterFetchState', false);
}

export async function fetchSingleEventFiltersAthleteById({ commit, dispatch }, {studentId, eventId}) {
    commit('setFetchState', true);
    commit('clearAllAthleteTeamFilters');
    const filterResponses = await Promise.all([
        api.get(`/api/v2/resources/athletes/${studentId}`, {
            query: {
                include: {
                    id: true,
                    firstName: true,
                    lastName: true,
                    positions: true,
                    classYear: true,
                    isVerified: true,
                    teamAffiliations: {
                        id: true,
                        team: {
                            id: true,
                            name: true,
                        }
                    }
                },
            }
        })
    ]);

    let athlete = denormalizeResponse(filterResponses[0]).root;

    const filters = [
        {
            title: "Teams",
            options: []
        },
        {
            title: "Athletes",
            options: [{
                value: athlete.id,
                // Not using the ds class here because I don't want line-height to be set.
                pillTitle: `${athlete.firstName} ${athlete.lastName}`,
                title: `${athlete.firstName} ${athlete.lastName}${athlete.classYear ? `, <span class="text-xs">${athlete.classYear}</span>` : ""}`,
                description: athlete.positions.join(", ") + "<br/>" + athlete.teamAffiliations.map(aff => aff.team.name).join(", "),
                selected: false,
                type: 'athletes',
                meta: {
                    isVerified: athlete.isVerified
                }
            }]
        },
    ];

    let filter = {
        pillTitle: `${athlete.firstName} ${athlete.lastName}`,
        title: `${athlete.firstName} ${athlete.lastName}${athlete.classYear ? `, <span class="text-xs">${athlete.classYear}</span>` : ""}`,
        type: "athletes",
        value: athlete.id
    };
    commit('setAvailableAthleteTeamFilters', filters);

    commit('setCountFetchState', true);
    commit("addAthleteTeamFilter", filter);

}

export async function fetchGamesFiltersAthleteById({ commit, dispatch }, studentId) {
    commit('setFetchState', true);
    commit('clearAllAthleteTeamFilters');
    const filterResponses = await Promise.all([
        api.get(`/api/v2/resources/athletes/${studentId}`, {
            query: {
                include: {
                    id: true,
                    firstName: true,
                    lastName: true,
                    positions: true,
                    classYear: true,
                    isVerified: true,
                    teamAffiliations: {
                        id: true,
                        team: {
                            id: true,
                            name: true,
                        }
                    }
                },
            }
        })
    ]);

    let athlete = denormalizeResponse(filterResponses[0]).root;

    const filters = [
        {
            title: "Teams",
            options: []
        },
        {
            title: "Athletes",
            options: [{
                value: athlete.id,
                // Not using the ds class here because I don't want line-height to be set.
                pillTitle: `${athlete.firstName} ${athlete.lastName}`,
                title: `${athlete.firstName} ${athlete.lastName}${athlete.classYear ? `, <span class="text-xs">${athlete.classYear}</span>` : ""}`,
                description: athlete.positions.join(", ") + "<br/>" + athlete.teamAffiliations.map(aff => aff.team.name).join(", "),
                selected: false,
                type: 'athletes',
                meta: {
                    isVerified: athlete.isVerified
                }
            }]
        },
    ];

    let filter = {
        pillTitle: `${athlete.firstName} ${athlete.lastName}`,
        title: `${athlete.firstName} ${athlete.lastName}${athlete.classYear ? `, <span class="text-xs">${athlete.classYear}</span>` : ""}`,
        type: "athletes",
        value: athlete.id
    };
    commit('setAvailableAthleteTeamFilters', filters);
    dispatch('applyGamesFilter', filter);
}

export async function applyEventFilter({ commit, dispatch }, filter = { type: null, value: null }) {
    commit('addEventsFilter', filter);
    commit('setEventPaginationData', {});
    dispatch('fetchEvents');
}

export async function removeEventFilter({ commit, dispatch }, filter = { type: null, value: null }) {
    commit('removeEventFilter', filter);
    dispatch('fetchEvents');
}

export async function clearAllEventFilters({ commit, dispatch }) {
    commit('clearAllEventFilters');
    dispatch('fetchEvents');
}

export async function applyGamesFilter({ commit, dispatch }, filter = { type: null, value: null }) {
    commit("addAthleteTeamFilter", filter);
    dispatch('fetchGames');
}

export async function removeGamesFilter({ commit, dispatch, state }, filter = { type: null, value: null }) {
    commit("removeAthleteTeamFilter", filter);
    if (state.appliedAthleteTeamFilters && state.appliedAthleteTeamFilters.length) {
        dispatch('fetchGames');
    } else {
        dispatch('fetchSixGames', { reset: true });
    }
}

export async function clearAllGameFilters({ commit, dispatch }) {
    commit('clearAllAthleteTeamFilters');
    dispatch('fetchSixGames', { reset: true });
}

export async function fetchRecommendedGames({ commit, state }) {
    const filters = {
        vodAvailable: true,
        hasGameVideo: true,
    };

    const response = await api.get(`/api/v2/resources/games`, {
        query: {
            filter: filters,
            limit: 10,
            include: {
                id: true,
                startTime: true,
                homeTeam: {
                    id: true,
                    name: true,
                },
                awayTeam: {
                    id: true,
                    name: true
                },
                favoritedAthleteCount: true,
                interestedAthleteCount: true,
                videos: {
                    id: true,
                    name: true,
                    host: true,
                    hostId: true,
                    duration: true,
                    thumbnailPath: true,
                    videoUrl: true,
                    videoType: true,
                },

            }
        }
    });

    commit('storeResources', response.resources);
    commit('setRecommendedGames', response.root);
}

export async function fetchSingleEvent({ commit, state, dispatch }, eventId) {
    commit('setCountFetchState', true);
    commit('setFetchState', true);

    countFetchStateTracker.push(true);

    dispatch('fetchSingleEventBasics', eventId);

    const filters = { vodAvailable: 1, inEvent: eventId, hasGameVideo: true };
    const appliedPlayersFilters = state.appliedAthleteTeamFilters ? state.appliedAthleteTeamFilters.filter(filter => filter.type == "athletes") : [];
    if (appliedPlayersFilters.length) {
        filters.hasOneOfAthletes = appliedPlayersFilters.map(filter => filter.value);
    }

    const appliedTeamsFilters = state.appliedAthleteTeamFilters ? state.appliedAthleteTeamFilters.filter(filter => filter.type == "teams") : [];

    if (appliedTeamsFilters.length) {
        filters.hasOneTeam = appliedTeamsFilters.map(filter => filter.value);
    }

    const appliedDivisionFilters = state.appliedAthleteTeamFilters ? state.appliedAthleteTeamFilters.filter(filter => filter.type == "division") : [];

    if (appliedDivisionFilters.length) {
        filters.hasOneOfDivisions = appliedDivisionFilters.map(filter => parseInt(filter.value));
    }

    const eventResponse = await api.get('/api/v2/resources/games', {
        query: {
            filter: filters,
            laravelPaginate: {
                perPage: 9,
                page: state.pagination.games.current_page,
            },
            sort: [
                {
                    dimension: "startTime",
                    direction: "asc",
                },
            ],
            include: {
                startTime: true,
                homeTeam: {
                    name: true,
                    division: {
                        id: true,
                        name: true,
                    }
                },
                awayTeam: {
                    name: true,
                    division: {
                        id: true,
                        name: true
                    }
                },
                favoritedAthleteCount: true,
                interestedAthleteCount: true,
                favoritedAthletes,
                interestedAthletes,
                videos: {
                    id: true,
                    name: true,
                    host: true,
                    hostId: true,
                    duration: true,
                    thumbnailPath: true,
                    videoUrl: true,
                    videoType: true,
                },
                event: {
                    id: true,
                    divisions: {
                        id: true,
                        name: true,
                    }
                }
            }
        }
    });

    let resources = {};
    if (eventResponse.resources && Object.keys(eventResponse.resources).length) {
        resources = {
            ...eventResponse.resources,
            events: {
                ...eventResponse.resources.events,
                [eventId]: {
                    ...eventResponse.resources.events[eventId],
                    gamesWithVideo: {
                        ...mapValues(eventResponse.resources.games, (game, key) => ({ type: 'games', id: key }))
                    }
                }
            }
        };
    } else if (eventResponse.root && !eventResponse.resources.length) {
        resources = {
            ...eventResponse.resources,
            events: {
                [eventId]: {
                    gamesWithVideo: {}
                }
            }
        };
    }

    commit('setGamePaginationData', eventResponse.meta);
    commit('storeResources', resources);
    commit('setFetchState', false);

    countFetchStateTracker.pop();
    
    if (!countFetchStateTracker.length) {
        commit('setCountFetchState', false);
    }
}

export async function fetchSingleEventBasics({ commit, state }, eventId) {
    const filters = { vodAvailable: 1, hasGameVideo: true };

    const eventResponse = await api.get(`/api/v2/resources/events/${eventId}`, {
        query: {
            filter: filters,
            include: {
                id: true,
                name: true,
                locationName: true,
                locationCity: true,
                locationState: true,
                startDate: true,
                endDate: true,
                interestedAthleteCount: true,
                favoritedAthleteCount: true,
                urlSlug: true,
                vodAvailable: true,
                gender: true,
                image: {
                    url: true,
                },
                sport: {
                    id: true,
                    name: true,
                }
            }
        }
    });

    commit('storeResources', eventResponse.resources);
    // commit('setFetchState', false);
}

export async function fetchSingleEventFilters({ commit }, { query = "", eventId }) {
    commit('setFilterFetchState', true);
    const filterResponses = await Promise.all([
        api.get('/api/v2/resources/athletes', {
            query: {
                filter: {
                    search: query,
                    attendingEvent: eventId,
                    attendingAnyEventWithVodAvailable: true,
                },
                include: {
                    id: true,
                    firstName: true,
                    lastName: true,
                    isVerified: true,
                    positions: true,
                    classYear: true,
                    teamAffiliations: {
                        id: true,
                        team: {
                            id: true,
                            name: true,
                        }
                    }
                },
            }
        }),
        api.get('/api/v2/resources/event-teams', {
            query: {
                filter: {
                    nameLike: `%${query.trim()}%`,
                    attendingEvent: eventId,
                    attendingAnyEventWithVodAvailable: true
                },
                include: {
                    id: true,
                    name: true,
                    event: {
                        name: true,
                        id: true
                    },
                    division: {
                        id: true,
                        name: true,
                    }
                }
            }
        })
    ]);

    let athletes = denormalizeResponse(filterResponses[0]).root.map(x => ({
        ...x,
        fullName: `${x.firstName} ${x.lastName}`
    }));
    athletes = sortByBestMatch(athletes, query, 'fullName');

    let teams = denormalizeResponse(filterResponses[1]).root;
    teams = sortByBestMatch(teams, query, 'name');

    const filters = buildFilter(teams, athletes);

    commit('setAvailableAthleteTeamFilters', filters);
    commit('setFilterFetchState', false);
}

function buildFilter(teams, athletes) {
    return [
        {
            title: "Teams",
            options: teams.map(team => ({
                value: team.id,
                title: `${team.name} ${team.division ? `(${team.division.name})` : ''}`,
                selected: false,
                type: 'teams',
            }))
        },
        {
            title: "Athletes",
            options: athletes.map(athlete => ({
                value: athlete.id,
                // Not using the ds class here because I don't want line-height to be set.
                pillTitle: `${athlete.firstName} ${athlete.lastName}`,
                title: `${athlete.firstName} ${athlete.lastName}${athlete.classYear ? `, <span class="text-xs">${athlete.classYear}</span>` : ""}`,
                description: athlete.positions.join(", ") + "<br/>" + athlete.teamAffiliations.map(aff => aff.team.name).join(", "),
                selected: false,
                type: 'athletes',
                meta: {
                    isVerified: athlete.isVerified
                }
            }))
        },
    ];
}

let singleEventFilterTimer = null;
let singleEventFetchQueue = new PromiseQueue();

function resetSingleEventFetchTimer() {
    if (singleEventFilterTimer) {
        clearTimeout(singleEventFilterTimer);
    }
}

function setSingleEventFetchTimer(params) {
    resetSingleEventFetchTimer();

    singleEventFilterTimer = setTimeout(() => {
        enqueueSingleEventFetch(params);
    }, 1000);
}

function enqueueSingleEventFetch({dispatch, eventId}) {
    singleEventFetchQueue.enqueue(() => dispatch('fetchSingleEvent', eventId));
}

export async function applySingleEventFilter({ commit, dispatch }, { filter, eventId }) {
    commit('setCountFetchState', true);
    commit("addAthleteTeamFilter", filter);
    commit('setGamePaginationData', {});
    setSingleEventFetchTimer({eventId, dispatch});
}

export async function applyMultipleSingleEventFilters({ commit, dispatch }, { filters = [], eventId }) {
    commit('setCountFetchState', true);
    filters.forEach(filter => {
        commit('addAthleteTeamFilter', filter);
    });

    setSingleEventFetchTimer({eventId, dispatch});
}

export async function removeSingleEventFilter({ commit, dispatch }, { filter, eventId }) {
    commit('setCountFetchState', true);
    commit('removeAthleteTeamFilter', filter);

    setSingleEventFetchTimer({eventId, dispatch});
}

export async function removeAllSingleEventFilterType({ commit, dispatch }, { type, eventId }) {
    commit('setCountFetchState', true);
    commit('removeAllAthleteTeamFilterType', type);

    setSingleEventFetchTimer({eventId, dispatch});
}

export async function clearAllSingleEventFilters({ commit, dispatch }, eventId) {
    commit('setCountFetchState', true);
    commit('clearAllAthleteTeamFilters');
    
    setSingleEventFetchTimer({eventId, dispatch});
}

export async function fetchEventFavoriteInterestedAthletes({ commit }, eventId) {
    commit('setFavIntFetchState', true);
    const response = await api.get(`/api/v2/resources/events/${eventId}`, {
        query: {
            include: {
                id: true,
                favoritedAthletes: {
                    id: true,
                    firstName: true,
                    lastName: true,
                    favoritesReceived: {
                        id: true
                    },
                    interests: {
                        id: true,
                    },
                    isVerified: true,
                    positions: true,
                    eventTeamAffiliations: {
                        id: true,
                        team: {
                            name: true,
                            event: {
                                id: true,
                            }
                        },
                    },
                    images: {
                        id: true,
                        isProfile: true,
                        url: true,
                    },
                    urlSlug: true,
                    classYear: true,
                },
                interestedAthletes: {
                    id: true,
                    firstName: true,
                    lastName: true,
                    favoritesReceived: {
                        id: true
                    },
                    interests: {
                        id: true,
                    },
                    isVerified: true,
                    positions: true,
                    eventTeamAffiliations: {
                        id: true,
                        team: {
                            name: true,
                            event: {
                                id: true
                            }
                        },
                    },
                    images: {
                        id: true,
                        isProfile: true,
                        url: true,
                    },
                    urlSlug: true,
                    classYear: true,
                }
            }
        }
    });
    commit('storeResources', response.resources);
    commit('setFavIntFetchState', false);
}